///
/// EarthBrowser
///
/// Created by Matt Giger on Dec 20, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type { VolcanoProperties } from "@/earthbrowser/dataset-distribution/volcanoes/VolcanoesTable"
import { VolcanoMarkerNode } from "@/earthbrowser/dataset-distribution/volcanoes/VolcanoMarkerNode"
import { EntityCameraAdapter } from "@/earthbrowser/datasets/EntityCameraAdapter"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic } from "@/earthbrowser/utils/geographic"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import type { Camera } from "@stovokor/camera/Camera"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { vec3 } from "@stovokor/math"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type DatasetSceneObject } from "../DatasetLayer"

const cVolcanoModelAltitude = 0.5
// Single radius for both the visible marker and the hit test sphere,
// so the glowing dome shows exactly the clickable volume
const cMarkerRadius = 3
const cVolcanoFocusDistance = 10
const cVolcanoFocusMaxZoomRadius = 100

export class VolcanoFeature extends GestureNode implements DatasetSceneObject<VolcanoProperties>, InfoPanelProvider {
	public readonly focusable: boolean = true
	public readonly id: string

	public feature: VolcanoProperties
	public readonly coordinate: geographic

	private readonly cameraAdapter: EntityCameraAdapter

	private readonly marker: VolcanoMarkerNode
	private readonly hitSphere = new sphere(cMarkerRadius)
	private readonly modelRotation = matrix.identity

	constructor(context: EarthBrowserContext, feature: VolcanoProperties) {
		super(context)
		this.feature = feature
		this.id = String(feature.id)

		const elevation = this.feature.elevation_m ?? cVolcanoModelAltitude * 1000
		const geo = new geographic(
			this.feature.lon * trig.rad,
			this.feature.lat * trig.rad,
			elevation / 1000
		)
		this.coordinate = geo

		const xRot = matrix.axisRotation(vec3.unitX, this.coordinate.latitude + trig.half_pi)
		const yRot = matrix.axisRotation(vec3.unitY, this.coordinate.longitude + trig.pi)
		this.modelRotation = yRot.premultiply(xRot)

		this.transform = matrix.translate(geo.cartesian)
			.premultiply(this.modelRotation)

		this.marker = new VolcanoMarkerNode(context, cMarkerRadius, this.feature.epoch)
		this.addNode(this.marker)

		this.cameraAdapter = new EntityCameraAdapter(context, this, cVolcanoFocusDistance, cVolcanoFocusMaxZoomRadius)
	}

	/**
	 * Update the feature data for this scene object
	 */
	public updateFeature(feature: VolcanoProperties): void {
		this.feature = feature
		this.marker.setEpoch(feature.epoch)
	}

	// ========== InfoPanelProvider ==========

	public getInfoPanelData(): InfoPanelData {
		return {
			title: this.feature.name ?? String(this.feature.id ?? "Volcano"),
			subtitle: this.feature.country || undefined,
			theme: "volcano",
			contentType: "volcano",
			contentData: {
				type: "volcano",
				volcano: {
					name: this.feature.name,
					number: this.feature.number,
					country: this.feature.country,
					region: this.feature.region,
					subregion: this.feature.subregion,
					latitude: this.feature.lat,
					longitude: this.feature.lon,
					elevation: this.feature.elevation_m ?? 0,
					type: this.feature.type,
					lastEruption: this.feature.last_eruption ?? "",
					epoch: this.feature.epoch ?? "",
					landform: this.feature.landform ?? undefined,
					tectonicSetting: this.feature.tectonic_setting ?? undefined,
					evidenceCategory: this.feature.evidence_category ?? undefined,
					rockType: this.feature.rock_type ?? undefined,
					geologicalSummary: this.feature.geological_summary ?? undefined,
				}
			}
		}
	}

	// ========== Focus Lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.cameraAdapter.attach(camera)
		this.marker.setFocused(true)

		// Push to browser history for back/forward navigation
		pushTargetToHistory('volcano', String(this.feature.id))
	}

	public onFocusLost(): void {
		this.cameraAdapter.detach()
		this.marker.setFocused(false)
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: 1,
				easing: "easeInOut"
			}
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		// Delegate camera and compass commands to the adapter
		return this.cameraAdapter.handleCommand(command, data)
	}

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		const dist = this.hitSphere.intersect(state.ray)
		if(dist > 0) {
			const hitPoint = state.ray.pointAt(dist)
			return { node: this, hitPoint: hitPoint, distance: dist }
		}
		return undefined
	}
}

